export const CONFIG = { spritePath: '/assets/effects/inferno_gauntlet/sprite.svg' };
const spriteCache: Record<string, HTMLImageElement> = {};

export const getSprite = (src: string): HTMLImageElement => {
    if (!spriteCache[src]) {
        const img = new Image();
        img.src = src;
        spriteCache[src] = img;
    }
    return spriteCache[src];
};
getSprite(CONFIG.spritePath); // Kickoff preload immediately

export interface InfernoParticle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    life: number;
    maxLife: number;
    size: number;
    r: number;
    g: number;
    b: number;
    isSpark?: boolean;
    isLava?: boolean;
}

export interface InfernoGauntletEffect {
    kind: 'infernoGauntlet';
    ox: number;
    oy: number;
    tx: number;
    ty: number;
    x: number;
    y: number;
    isOpponent: boolean;
    state: 'windup' | 'travel' | 'impact';
    t: number;
    particles: InfernoParticle[];
}

export function createInfernoGauntletEffect(ox: number, oy: number, tx: number, ty: number, isOpponent: boolean): InfernoGauntletEffect {
    return {
        kind: 'infernoGauntlet',
        ox, oy, tx, ty,
        x: ox, y: oy,
        isOpponent,
        state: 'windup',
        t: 0,
        particles: []
    };
}

function spawnFire(particles: InfernoParticle[], px: number, py: number, speed: number, count: number, type: 'fire' | 'spark' | 'lava' = 'fire') {
    for (let i = 0; i < count; i++) {
        const angle = Math.random() * Math.PI * 2;
        const radius = Math.random() * speed;

        let r, g, b;
        let isSpark = type === 'spark';
        let isLava = type === 'lava';

        if (isSpark) {
            r = 255; g = 220; b = 50; // bright yellow/white
        } else if (isLava) {
            r = 255; g = 100 + Math.random() * 50; b = 0; // dense orange/red
        } else {
            if (Math.random() > 0.5) {
                r = 239; g = 68; b = 68; // red
            } else {
                r = 249; g = 115; b = 22; // orange
            }
        }

        particles.push({
            x: px + (Math.random() - 0.5) * (isLava ? 40 : 20),
            y: py + (Math.random() - 0.5) * (isLava ? 40 : 20),
            vx: Math.cos(angle) * radius,
            vy: Math.sin(angle) * radius - (isSpark ? 2 : 0) + (isLava ? -Math.random() * 5 : 0), // Lava explodes upwards initially
            life: isSpark ? 30 + Math.random() * 20 : (isLava ? 50 + Math.random() * 40 : 15 + Math.random() * 15),
            maxLife: isLava ? 100 : 50,
            size: isSpark ? 2 + Math.random() * 3 : (isLava ? 15 + Math.random() * 25 : 10 + Math.random() * 20),
            r, g, b,
            isSpark,
            isLava
        });
    }
}

export function renderInfernoGauntletEffect(effect: InfernoGauntletEffect, ctx: CanvasRenderingContext2D): boolean {
    effect.t++;

    // Logic updates
    if (effect.state === 'windup') {
        // Gathering energy
        if (Math.random() > 0.2) spawnFire(effect.particles, effect.x, effect.y, 3, 3, 'fire');

        if (effect.t > 800) { // 800ms windup (slower buildup)
            effect.state = 'travel';
            effect.t = 0;
        }
    } else if (effect.state === 'travel') {
        const totalTravelTime = 600; // 600ms rocket travel
        const MathProg = effect.t / totalTravelTime;
        const prog = MathProg > 1 ? 1 : MathProg;

        // Custom easeInBack curve (much softer snapback)
        // Instead of hard sin waves or aggressive constants, use a standard gentler backEaseIn
        // c1 determines the severity of the pull-back. 1.70158 is massive. We'll use 0.8.
        const c1 = 0.8;
        const ease = (c1 + 1) * prog * prog * prog - c1 * prog * prog;

        effect.x = effect.ox + (effect.tx - effect.ox) * ease;
        effect.y = effect.oy + (effect.ty - effect.oy) * ease;

        if (Math.random() > 0.2) spawnFire(effect.particles, effect.x, effect.y, 2, 4, 'fire'); // thick trail
        if (Math.random() > 0.4) spawnFire(effect.particles, effect.x, effect.y, 6, 2, 'spark'); // sparks fly off

        if (effect.t >= totalTravelTime) {
            effect.state = 'impact';
            effect.t = 0;

            // Central blinding flash
            spawnFire(effect.particles, effect.tx, effect.ty, 2, 5, 'spark');
            // Rings of fire
            spawnFire(effect.particles, effect.tx, effect.ty, 15, 30, 'fire');
            // Heavy outwards lava
            spawnFire(effect.particles, effect.tx, effect.ty, 18, 25, 'lava');
            // Massive fast sparks
            spawnFire(effect.particles, effect.tx, effect.ty, 35, 40, 'spark');
        }
    } else if (effect.state === 'impact') {
        if (effect.t < 150) {
            // Aftershock eruptions
            if (Math.random() > 0.4) spawnFire(effect.particles, effect.tx, effect.ty, 8, 8, 'lava');
        }
        if (effect.t > 1500 && effect.particles.length === 0) { // 1500ms
            return false;
        }
    }

    // Render Particles
    ctx.save();

    // Impact Shockwave (rendered beneath particles)
    if (effect.state === 'impact' && effect.t < 400) {
        const swProg = effect.t / 400;
        const swRadius = swProg * 350; // Expands to 350px
        const swAlpha = 1 - swProg;

        ctx.beginPath();
        ctx.arc(effect.tx, effect.ty, swRadius, 0, Math.PI * 2);
        ctx.lineWidth = 15 * (1 - swProg);
        ctx.strokeStyle = `rgba(255, 150, 0, ${swAlpha})`;
        ctx.stroke();

        // Inner intense wave
        ctx.beginPath();
        ctx.arc(effect.tx, effect.ty, swRadius * 0.8, 0, Math.PI * 2);
        ctx.lineWidth = 30 * (1 - swProg);
        ctx.strokeStyle = `rgba(255, 50, 0, ${swAlpha * 0.5})`;
        ctx.stroke();
    }
    ctx.globalCompositeOperation = 'lighter';
    for (let i = effect.particles.length - 1; i >= 0; i--) {
        const p = effect.particles[i];
        p.life--;
        if (p.life <= 0) {
            effect.particles[i] = effect.particles[effect.particles.length - 1];
            effect.particles.pop();
            continue;
        }

        p.x += p.vx;
        p.y += p.vy;
        if (p.isLava) {
            p.vy += 0.4; // heavy gravity for magma blobs
            p.vx *= 0.92; // drag
            p.size *= 0.98; // cools down slowly
        } else if (p.isSpark) {
            p.vy += 0.05; // light gravity for spark
            p.vx *= 0.95; // drag
        } else {
            p.size *= 0.88; // shrink fire fast
            p.vy -= 0.2; // fire rises quickly
        }

        const alpha = Math.max(0, p.life / p.maxLife);
        ctx.fillStyle = `rgba(${p.r}, ${p.g}, ${p.b}, ${alpha})`;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();

        // Add a bright core to lava
        if (p.isLava && p.size > 8 && alpha > 0.3) {
            ctx.fillStyle = `rgba(255, 255, 150, ${alpha})`;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * 0.4, 0, Math.PI * 2);
            ctx.fill();
        }
    }
    ctx.restore();

    // Render Sprite
    // Stays visible during windup and travel.
    if (effect.state === 'windup' || effect.state === 'travel') {
        const img = getSprite(CONFIG.spritePath);
        if (img.complete && img.naturalWidth > 0) {
            ctx.save();

            let drawX = effect.x;
            let drawY = effect.y;
            let scale = 1;
            let dropAngle = 0;

            if (effect.state === 'windup') {
                const wProg = effect.t / 800; // 0 to 1 over 800ms

                // Fly in from outside bounds toward the owner avatar
                const startX = effect.ox + (effect.isOpponent ? -300 : 300);
                const startY = effect.oy + (effect.isOpponent ? -400 : 400);

                // Smoother easeOutCubic instead of aggressive snapping
                const flyEase = 1 - Math.pow(1 - wProg, 3);

                drawX = startX + (effect.ox - startX) * flyEase;
                drawY = startY + (effect.oy - startY) * flyEase;

                // Scale starts large and gently shrinks
                scale = 2.5 - (1.5 * flyEase);

                // Gentle rotation into place
                dropAngle = (1 - flyEase) * Math.PI;

                // Keep effect.x and y synced to the owner avatar during windup so particles spawn correctly
                effect.x = effect.ox;
                effect.y = effect.oy;
            }

            ctx.translate(drawX, drawY);

            // Faux 3D drop shadow
            ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
            ctx.shadowBlur = 25 * scale;
            ctx.shadowOffsetY = 20 * scale;

            if (effect.isOpponent) {
                ctx.scale(1, -1);
            }

            // Apply entry rotation + continuous wobble
            const angle = dropAngle + Math.sin(Date.now() * 0.008) * 0.1;
            ctx.rotate(angle);

            ctx.scale(scale, scale);

            // Draw it scaled to fit well visually
            ctx.drawImage(img, -75, -75, 150, 150);
            ctx.restore();
        }
    }
    // Note: Sprite is NOT drawn in 'impact' state. It disappears instantly behind the lava explosion.

    return true;
}
